import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, X, Lock } from "lucide-react";
import { useAuth } from "../lib/auth";

const CAP_KEY = {
  Access: "can_access", View: "can_view", Create: "can_create",
  Edit: "can_edit", Delete: "can_delete", Export: "can_export", Configure: "can_configure",
  "Edit User Email": "can_edit_user_email",
  "Edit Self Email": "can_edit_self_email",
};

export default function PermissionMatrixPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activeRole, setActiveRole] = useState("Project Manager");

  const load = () => api.get("/permissions").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;

  const rulesByKey = {};
  data.rules.forEach((r) => { rulesByKey[`${r.role}|${r.module_name}`] = r; });

  // Build-phase rule: Project Manager can edit CEO permissions too.
  // Only Administration / HM / Staff are locked out of CEO row (those roles can't even reach this page anyway).
  const isLocked = (role) => role === "CEO" && !["CEO", "Project Manager"].includes(user.access_role);

  const toggle = async (role, module_name, capability_name, current) => {
    if (isLocked(role)) return toast.error("Only CEO or Project Manager can edit CEO permissions.");
    try {
      await api.post("/permissions/toggle", { role, module_name, capability_name, value: !current });
      toast.success("Permission updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div>
        <p className="text-small-caps">Settings</p>
        <h1 className="text-h1 mt-1">Permission Matrix</h1>
        <p className="text-sm text-slate-500 mt-1">Toggle capabilities per role. Changes save immediately and are logged.</p>
      </div>

      {user.access_role === "Project Manager" && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-testid="pm-buildphase-banner"
        >
          <strong>Build-phase note:</strong> Project Manager can temporarily edit CEO record and CEO permissions to fix setup issues. The system blocks deletion of the CEO and prevents removing the CEO's last remaining permission. Re-tighten before production go-live.
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="role-tabs">
        {data.roles.map((r) => (
          <button
            key={r}
            onClick={() => setActiveRole(r)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition ${activeRole === r ? "bg-sky-800 text-white border-sky-800" : "bg-white text-slate-600 border-slate-200 hover:border-sky-300"} ${isLocked(r) ? "opacity-70" : ""}`}
            data-testid={`role-tab-${r.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {isLocked(r) && <Lock size={11} className="inline mr-1" />} {r}
          </button>
        ))}
      </div>

      <div className="nv-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="permission-matrix-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 text-left w-56">Module</th>
              {data.capabilities.map((c) => <th key={c} className="px-3 py-3 text-center">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.modules.map((mod) => {
              const rule = rulesByKey[`${activeRole}|${mod}`] || {};
              return (
                <tr key={mod} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-medium text-slate-800">{mod}</td>
                  {data.capabilities.map((cap) => {
                    const isEmailCap = cap === "Edit User Email" || cap === "Edit Self Email";
                    if (isEmailCap && mod !== "User Management") {
                      return <td key={cap} className="px-3 py-3 text-center text-slate-300">—</td>;
                    }
                    const val = !!rule[CAP_KEY[cap]];
                    return (
                      <td key={cap} className="px-3 py-3 text-center">
                        <button
                          onClick={() => toggle(activeRole, mod, cap, val)}
                          disabled={isLocked(activeRole)}
                          className={`h-7 w-7 rounded-md inline-flex items-center justify-center transition ${val ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-slate-100 text-slate-400 hover:bg-slate-200"} ${isLocked(activeRole) ? "opacity-60 cursor-not-allowed" : ""}`}
                          data-testid={`perm-${activeRole}-${mod}-${cap}`.replace(/\s+/g, "_")}
                        >
                          {val ? <Check size={14} /> : <X size={14} />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
